/**
 * Weighted scoring & grade definition contract — E07-F01. Shared by api (the
 * scoring/grade-explain/weight-profile services + the admin route) and web (the
 * Settings weight-profile preview + the Intake grade-scope cards).
 *
 * Parity source (1:1 port, PM ground truth):
 *   - docs/source-n8n/.. has no live Stage-2 prompt body (companies/research_*
 *     was NEVER deployed to legacy n8n prod), so the authoritative source is the
 *     research prototype `../EER app/prototype/assets/research.data.js`
 *     (MOCK.weights / MOCK.criteriaKeys / MOCK.companies — the 15 hand-graded
 *     rows: Cynet A, Arctic Wolf A with founded=fail, Sygnia B near-miss,
 *     NTT/Microsoft division-of, Pentera/Seemplicity Grade-C filler, SecHerd /
 *     BlueOps / Talon needs_human) + company-research-architecture.md §6.16, §9
 *     + the ADR 2026-06-02 grading philosophy (request-driven; services_match is
 *     ONE weighted signal, NOT a hard gate).
 *   - The prompt KEYS (research_score_candidate / research_grade_explain) are in
 *     prisma/seed-data.ts and loaded from `ai_prompts` at runtime
 *     (prompts-only-from-DB; a hardcoded prompt body is FORBIDDEN here).
 *
 * INVARIANTS encoded here (from the E07-F01 acceptance criteria):
 *   - weight profile weights MUST sum to exactly 100 (else VALIDATION_ERROR).
 *   - criteria keys = services, subIndustry, geo, size, founded, industry.
 *   - contribution_k = weight_k × match(verdict_k), match: pass=1.0 partial=0.5
 *     fail=0.0; pctMatch = Σ contribution / Σ weight × 100.
 *   - founded=fail at 10% LOWERS the score but does NOT drop Grade A (Arctic Wolf).
 *   - HARD filters (geo radius outside / sizeLocalCap over) are binary gates;
 *     services_match is never a hard gate.
 *   - confidence NUMERIC(4,3); needs_human=true when confidence < 0.5, OR no
 *     reachable site, OR ambiguous site, OR manual row, OR a PASS/PARTIAL
 *     criterion lacks a verbatim citation.
 *   - grade_scope: 'A' → only A; 'A+B' → A,B; 'A+B+C' → + auto Grade-C filler.
 *   - extract-division: conglomerate is scored on the EXTRACTED unit, not parent.
 *
 * This file is feature-owned. It is re-exported from `@eer/shared` by the serial
 * Wire pass — it MUST NOT edit the shared barrel during parallel work.
 */
import { z } from 'zod';
// `GRADE_SCOPES`/`gradeScopeSchema`/`GradeScope` are the SAME canonical binding as
// in `jsonb.ts` (the intake grade-scope vocab). Re-export them from there so there
// is ONE definition (no TS2308 in the shared barrel) and a single source of truth.
import { GRADE_SCOPES, gradeScopeSchema } from '../jsonb.js';
export { GRADE_SCOPES, gradeScopeSchema };
// ---------------------------------------------------------------------------
// Criteria keys + weights (the request's weight profile)
// ---------------------------------------------------------------------------
/**
 * The 6 weighted criteria keys, in the canonical UI order used to render the
 * weight-profile bars in Settings and the breakdown mini-chips in Review.
 * Verbatim from research.data.js MOCK.criteriaKeys (display order) but the weight
 * map below uses the same keys as MOCK.weights.
 */
export const SCORING_CRITERIA_KEYS = [
    'services',
    'subIndustry',
    'geo',
    'size',
    'founded',
    'industry',
];
/** Display order + label for the Settings weight-profile preview bars. */
export const SCORING_CRITERIA_DISPLAY = [
    { key: 'services', label: 'Services match (About-Us)' },
    { key: 'subIndustry', label: 'Sub-industry' },
    { key: 'geo', label: 'Geo (radius)' },
    { key: 'size', label: 'Company size' },
    { key: 'founded', label: 'Founded window' },
    { key: 'industry', label: 'Industry' },
];
/**
 * Default weight profile — verbatim from research.data.js MOCK.weights and
 * prisma/seed-data.ts SEED_WEIGHT_PROFILE.weights. Sums to exactly 100.
 */
export const DEFAULT_SCORING_WEIGHTS = {
    services: 35,
    subIndustry: 25,
    geo: 15,
    size: 10,
    founded: 10,
    industry: 5,
};
/** Per-verdict match factor (the weighted-sum coefficients). */
export const SCORING_VERDICTS = ['pass', 'partial', 'fail'];
export const scoringVerdictSchema = z.enum(SCORING_VERDICTS);
/** match factor: pass=1.0, partial=0.5, fail=0.0 (research.data.js comment). */
export const SCORING_MATCH_FACTOR = {
    pass: 1.0,
    partial: 0.5,
    fail: 0.0,
};
/** Required sum of every weight profile. */
export const WEIGHTS_SUM = 100;
/** A weight map keyed by the 6 criteria. Must contain every key, integer ≥ 0. */
export const weightProfileSchema = z
    .object({
    services: z.number().int().min(0),
    subIndustry: z.number().int().min(0),
    geo: z.number().int().min(0),
    size: z.number().int().min(0),
    founded: z.number().int().min(0),
    industry: z.number().int().min(0),
})
    .strict();
// ---------------------------------------------------------------------------
// Grade scope (Intake) — A | A+B | A+B+C
// (GRADE_SCOPES / gradeScopeSchema / GradeScope re-exported from ../jsonb.js above)
// ---------------------------------------------------------------------------
export const SCORING_GRADES = ['A', 'B', 'C'];
export const scoringGradeSchema = z.enum(SCORING_GRADES);
// ---------------------------------------------------------------------------
// Breakdown (per-criterion verdicts)
// ---------------------------------------------------------------------------
/** research_companies.breakdown — {criterion: pass|partial|fail}. */
export const scoringBreakdownSchema = z.record(z.enum(SCORING_CRITERIA_KEYS), scoringVerdictSchema);
// ---------------------------------------------------------------------------
// Hard-gate input (geo radius / size cap) — binary, not weighted
// ---------------------------------------------------------------------------
/** Inputs the binary hard-filter gates consult (geo radius / size cap). */
export const hardGateInputSchema = z.object({
    /** True when the company sits OUTSIDE the allowed geo radius (city-search only). */
    geoOutOfZone: z.boolean().default(false),
    /** Whether a metro radius is even in effect (false for whole-country/multi-region). */
    metroRadiusActive: z.boolean().default(false),
    /** Local headcount, compared against sizeLocalCap. */
    localSize: z.number().nullable().default(null),
    /** Geo-bound local cap (e.g. "≤250 in Israel"); null → no cap. */
    sizeLocalCap: z.number().nullable().default(null),
    /** Company / industry hits a DNS exclusion → never on the long-list. */
    dnsExcluded: z.boolean().default(false),
});
// ---------------------------------------------------------------------------
// needs_human signals (T05)
// ---------------------------------------------------------------------------
/** The structured signals that can raise needs_human (besides confidence<0.5). */
export const needsHumanSignalsSchema = z.object({
    /** No reachable first-party website (fit inferred from a directory — SecHerd). */
    noReachableSite: z.boolean().default(false),
    /** Site copy ambiguous between managed MDR and project consulting (BlueOps). */
    ambiguousSite: z.boolean().default(false),
    /** Row was added manually by a specialist (Talon — may be unscored). */
    isManual: z.boolean().default(false),
    /** A PASS/PARTIAL criterion is missing a verbatim citation (quote/url). */
    missingCitationForPositive: z.boolean().default(false),
});
/** confidence below this → needs_human (acceptance: <0.5). */
export const CONFIDENCE_THRESHOLD = 0.5;
// ---------------------------------------------------------------------------
// Division grading (T08)
// ---------------------------------------------------------------------------
export const SCORING_ENTITY_TYPES = ['pure-play', 'division-of', 'portfolio-item'];
export const scoringEntityTypeSchema = z.enum(SCORING_ENTITY_TYPES);
export const SCORING_INCLUDE_DECISIONS = ['whole', 'division', 'exclude'];
export const scoringIncludeDecisionSchema = z.enum(SCORING_INCLUDE_DECISIONS);
/** Disambiguation context that steers scoring onto the EXTRACTED unit (T08). */
export const divisionContextSchema = z.object({
    entityType: scoringEntityTypeSchema,
    includeDecision: scoringIncludeDecisionSchema,
    /** The extracted matching unit name (e.g. "Defender Experts for XDR"). */
    resolvedEntity: z.string().nullable().default(null),
    /** Parent conglomerate name (for the rationale note). */
    parentName: z.string().nullable().default(null),
});
// ---------------------------------------------------------------------------
// Scoring result (the per-candidate decision)
// ---------------------------------------------------------------------------
/** Result of scoring ONE candidate (one of a 20-30 batch). */
export const scoringResultSchema = z.object({
    /** The per-criterion verdicts that fed the score. */
    breakdown: scoringBreakdownSchema,
    /** Σ(weight×match) of every criterion (informational; used in the evidence drawer). */
    contributions: z.record(z.enum(SCORING_CRITERIA_KEYS), z.number()),
    /** 0..100 weighted match, NUMERIC(5,2); null when hard-excluded / unscored. */
    score: z.number().min(0).max(100).nullable(),
    /** A|B|C|null (null = excluded, below the Grade-C cut, or scope-clipped). */
    grade: scoringGradeSchema.nullable(),
    /** True when a hard gate (geo radius / size cap / DNS) excluded the row. */
    excluded: z.boolean(),
    /** True when this is an auto Grade-C long-list filler (outside A/B defs). */
    isGradeCFiller: z.boolean().default(false),
    /** True when the scored unit is an extracted division, not the parent (T08). */
    scopedToDivision: z.boolean().default(false),
    /** confidence NUMERIC(4,3) 0..1. */
    confidence: z.number().min(0).max(1),
    /** needs_human verdict (confidence<0.5 OR a structured signal). */
    needsHuman: z.boolean(),
    /** One-line request-tied rationale (research_grade_explain output). */
    rationale: z.string(),
});
// ---------------------------------------------------------------------------
// Score-candidate input (one batch item)
// ---------------------------------------------------------------------------
/** One candidate to score (services_match is a weighted signal, not a gate). */
export const scoreCandidateInputSchema = z.object({
    /** research_companies.id (the long-list row). */
    rowId: z.number().int().positive().optional(),
    name: z.string(),
    breakdown: scoringBreakdownSchema,
    gate: hardGateInputSchema.partial().optional(),
    needsHumanSignals: needsHumanSignalsSchema.partial().optional(),
    division: divisionContextSchema.partial().optional(),
    confidence: z.number().min(0).max(1).optional(),
    /** Pre-built rationale (from research_grade_explain); fallback computed if absent. */
    rationale: z.string().optional(),
});
/** The batch payload sent to the scoring stage (20-30 companies). */
export const SCORING_BATCH_MIN = 20;
export const SCORING_BATCH_MAX = 30;
// ---------------------------------------------------------------------------
// grade-explain (research_grade_explain) prompt vars + output
// ---------------------------------------------------------------------------
/** The variables substituted into the research_grade_explain prompt (per row). */
export const gradeExplainVarsSchema = z.object({
    name: z.string(),
    grade: scoringGradeSchema.nullable(),
    score: z.number().nullable(),
    breakdown: scoringBreakdownSchema,
    /** Recruiter's Grade A/B/C definitions + free-text (request-tied, not site copy). */
    gradeDefinitions: z
        .object({ A: z.string().optional(), B: z.string().optional(), C: z.string().optional() })
        .default({}),
    freeText: z.string().optional(),
    /** Division note seed (T08) — non-empty ⇒ rationale flags the extracted unit. */
    divisionNote: z.string().optional(),
    /** Grade-C filler flag (T09) — true ⇒ rationale flags long-list filler. */
    isGradeCFiller: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// REST contracts — admin weight profile + per-request override + grade-scope
// ---------------------------------------------------------------------------
/** GET /research/scoring/weight-profile — the active default profile + preview. */
export const weightProfileResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    profileKey: z.string(),
    displayName: z.string(),
    weights: weightProfileSchema,
    /** Settings preview bars (label + width%); width = weight × 2 (research proto). */
    preview: z.array(z.object({
        key: z.enum(SCORING_CRITERIA_KEYS),
        label: z.string(),
        weight: z.number(),
        widthPct: z.number(),
    })),
});
/** PUT /research/scoring/weight-profile (admin) — save weights (sum=100). */
export const saveWeightProfileBodySchema = z.object({
    weights: weightProfileSchema,
});
/** POST /research/scoring/validate-override — validate a per-request override. */
export const validateOverrideBodySchema = z.object({
    weights: weightProfileSchema,
});
export const validateOverrideResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    /** The validated snapshot (echoed back; written into research_requests.weight_profile). */
    snapshot: weightProfileSchema,
});
/** GET /research/scoring/grade-scope/:scope — which definition cards are visible. */
export const gradeScopeVisibilityResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    scope: gradeScopeSchema,
    /** Cards the Intake form shows for this scope (applyGradeScope). */
    visibleGrades: z.array(scoringGradeSchema),
    /** Whether the engine auto-generates Grade-C filler for this scope. */
    cFillerEnabled: z.boolean(),
});
//# sourceMappingURL=research-scoring.js.map